'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { handleApiError } from '@/lib/error-handler';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ActionBadge } from '@/components/iam/ActionBadge';
import {
  queryIamAuditLogs,
  type IamAuditAction,
  type IamAuditLog,
} from '@/services/api/iam-admin';

const ACTIONS: Array<IamAuditAction> = ['CREATE', 'UPDATE', 'DELETE', 'ADMIN_BYPASS'];
const PAGE_SIZE = 20;
// shadcn Select 不接受空字符串值，用专用哨兵值表示"全部"
const ALL_VALUE = '__all__';

export default function AuditLogsPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<IamAuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [actor, setActor] = useState('');
  const [action, setAction] = useState<string>('');
  const [resource, setResource] = useState('');
  const [showJson, setShowJson] = useState<IamAuditLog | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await queryIamAuditLogs({
        actor: actor.trim() || undefined,
        action: (action || undefined) as IamAuditAction | undefined,
        resource: resource.trim() || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      setItems(r.items);
      setTotal(r.total);
    } catch (e) {
      handleApiError(e, t.locale, { prefix: t.iamAdmin.common.loadFailed });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [page]);

  const onSearch = () => {
    // 已经在 page=1 时直接刷；否则只 setPage 让 effect 触发，避免双发请求
    if (page === 1) reload();
    else setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-gray-900">{t.iamAdmin.auditLogs.title}</h1>
        <p className="text-sm text-gray-500">{t.iamAdmin.auditLogs.subtitle}</p>
      </div>

      <div className="mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs text-gray-500">{t.iamAdmin.auditLogs.filters.actor}</Label>
          <Input value={actor} onChange={(e) => setActor(e.target.value)} placeholder="UUID" />
        </div>
        <div>
          <Label className="text-xs text-gray-500">{t.iamAdmin.auditLogs.filters.action}</Label>
          <Select value={action || ALL_VALUE} onValueChange={(v) => setAction(v === ALL_VALUE ? '' : v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_VALUE}>{t.iamAdmin.common.all}</SelectItem>
              {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-gray-500">{t.iamAdmin.auditLogs.filters.resource}</Label>
          <Input value={resource} onChange={(e) => setResource(e.target.value)} placeholder="DataScope / RoleDataScope / ..." />
        </div>
        <div className="flex items-end">
          <Button onClick={onSearch}>{t.iamAdmin.auditLogs.search}</Button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t.iamAdmin.auditLogs.columns.timestamp}</TableHead>
              <TableHead>{t.iamAdmin.auditLogs.columns.action}</TableHead>
              <TableHead>{t.iamAdmin.auditLogs.columns.resource}</TableHead>
              <TableHead>{t.iamAdmin.auditLogs.columns.actor}</TableHead>
              <TableHead>{t.iamAdmin.auditLogs.columns.targetId}</TableHead>
              <TableHead className="text-right">{t.iamAdmin.common.actions}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">{t.iamAdmin.common.loading}</TableCell></TableRow>}
            {!loading && items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">{t.iamAdmin.common.empty}</TableCell></TableRow>}
            {items.map((it) => (
              <TableRow key={it.id}>
                <TableCell className="text-sm whitespace-nowrap">{new Date(it.timestamp).toLocaleString()}</TableCell>
                <TableCell><ActionBadge action={it.action} /></TableCell>
                <TableCell className="font-mono text-xs">{it.resource}</TableCell>
                <TableCell className="font-mono text-xs">{it.actor.slice(0, 8)}…</TableCell>
                <TableCell className="font-mono text-xs">{it.targetId?.slice(0, 8) ?? '-'}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => setShowJson(it)}>
                    {t.iamAdmin.auditLogs.viewDiff}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
        <div>
          {t.iamAdmin.auditLogs.totalLabel}: {total}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            {t.iamAdmin.auditLogs.prev}
          </Button>
          <span>{page} / {totalPages}</span>
          <Button size="sm" variant="outline" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
            {t.iamAdmin.auditLogs.next}
          </Button>
        </div>
      </div>

      <Dialog open={!!showJson} onOpenChange={(o) => !o && setShowJson(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t.iamAdmin.auditLogs.diffTitle}</DialogTitle>
          </DialogHeader>
          {showJson && (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="font-medium text-gray-700 mb-1">{t.iamAdmin.auditLogs.diffBefore}</div>
                <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto max-h-96">
                  {showJson.before ? JSON.stringify(showJson.before, null, 2) : '—'}
                </pre>
              </div>
              <div>
                <div className="font-medium text-gray-700 mb-1">{t.iamAdmin.auditLogs.diffAfter}</div>
                <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto max-h-96">
                  {showJson.after ? JSON.stringify(showJson.after, null, 2) : '—'}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
